import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Alert,
  Grid,
  Autocomplete,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  MenuItem,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ContentCopy as DuplicateIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import {
  useFeedTypes,
  useFeedBrands,
  useRawMaterials,
  useFeedProductionTemplates,
  useCreateFeedTemplate,
  useUpdateFeedTemplate,
  useDeleteFeedTemplate,
} from '../hooks/useFeed';
import { useBirdProcurements, useBirds } from '../hooks/useBirds';

interface MaterialRow {
  raw_material_id: number;
  quantity_per_tonne: number;
  required?: boolean;
  concentrateOnly?: boolean;
}

const REQUIRED_MATERIAL_NAMES = ['Corn', 'Soybean', 'Wheat bran', 'Shell', 'Fish'];
const CONCENTRATE_NAMES = ['Essaar', 'Maridav', 'Koudjis', 'Jubaili', 'Intraco'];

// A "concentrate" must carry the brand name AND the word "concentrate".
// Products like "Maridav Layer Premix" share the brand but are regular additives.
const isConcentrateMaterial = (name: string) => {
  const n = name.toLowerCase();
  return n.includes('concentrate') && CONCENTRATE_NAMES.some((cn) => n.includes(cn.toLowerCase()));
};

function buildRequiredRows(rawMaterials: any[]): MaterialRow[] {
  const rows: MaterialRow[] = REQUIRED_MATERIAL_NAMES.map((name) => {
    const mat = rawMaterials.find((m) => m.name.toLowerCase().includes(name.toLowerCase()));
    return {
      raw_material_id: mat?.id ?? 0,
      quantity_per_tonne: 0,
      required: true,
      concentrateOnly: false,
    };
  });
  rows.push({ raw_material_id: 0, quantity_per_tonne: 0, required: true, concentrateOnly: true });
  return rows;
}

const emptyForm = {
  name: '',
  description: '',
  feed_type_id: 0,
  feed_brand_id: 0,
  bird_procurement_ids: [] as number[],
  bird_ids: [] as number[],
  default_quantity_kg: '',
  other_costs: '0',
  notes: '',
  is_active: true,
};

export default function FeedProductionTemplates() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [materials, setMaterials] = useState<MaterialRow[]>([
    { raw_material_id: 0, quantity_per_tonne: 0 },
  ]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const queryClient = useQueryClient();

  const { data: feedTypes = [] } = useFeedTypes();
  const { data: feedBrands = [] } = useFeedBrands();
  const { data: rawMaterials = [] } = useRawMaterials();

  useEffect(() => {
    if (dialogOpen) {
      queryClient.invalidateQueries({ queryKey: ['feed', 'raw-materials'] });
    }
  }, [dialogOpen, queryClient]);
  const { data: templates = [], isFetching } = useFeedProductionTemplates();
  const { data: birdProcurementsData } = useBirdProcurements();
  const birdProcurements = birdProcurementsData?.data ?? [];

  const { data: allBirdsData } = useBirds({ per_page: 200 });
  const allBirds = allBirdsData?.data ?? [];

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (templates as any[]).filter((tpl) => {
      if (statusFilter === 'active' && !tpl.is_active) return false;
      if (statusFilter === 'inactive' && tpl.is_active) return false;
      if (!q) return true;
      return (
        tpl.name?.toLowerCase().includes(q) ||
        tpl.feed_type?.name?.toLowerCase().includes(q) ||
        tpl.feed_brand?.name?.toLowerCase().includes(q) ||
        (tpl.bird_procurements ?? []).some((p: any) => p.identifier?.toLowerCase().includes(q)) ||
        tpl.description?.toLowerCase().includes(q)
      );
    });
  }, [templates, search, statusFilter]);

  // IDs of raw materials already selected in other rows — used to exclude them per row
  const selectedMaterialIds = useMemo(
    () => new Set(materials.map((m) => m.raw_material_id).filter((id) => id > 0)),
    [materials]
  );

  const createMutation = useCreateFeedTemplate();
  const updateMutation = useUpdateFeedTemplate();
  const deleteMutation = useDeleteFeedTemplate();

  const resetForm = () => {
    setFormData({ ...emptyForm });
    setMaterials([{ raw_material_id: 0, quantity_per_tonne: 0 }]);
    setEditingTemplate(null);
    setError('');
  };

  const openCreate = () => {
    setFormData({ ...emptyForm });
    setEditingTemplate(null);
    setError('');
    setMaterials([
      ...buildRequiredRows(rawMaterials as any[]),
      { raw_material_id: 0, quantity_per_tonne: 0 },
    ]);
    setDialogOpen(true);
  };

  const openEdit = (tpl: any) => {
    setEditingTemplate(tpl);
    setFormData({
      name: tpl.name,
      description: tpl.description ?? '',
      feed_type_id: tpl.feed_type_id,
      feed_brand_id: tpl.feed_brand_id,
      bird_procurement_ids: (tpl.bird_procurements ?? []).map((p: any) => p.id),
      bird_ids: (tpl.birds ?? []).map((b: any) => b.id),
      default_quantity_kg: tpl.default_quantity_kg
        ? String(parseInt(tpl.default_quantity_kg, 10))
        : '',
      other_costs: tpl.other_costs ?? '0',
      notes: tpl.notes ?? '',
      is_active: tpl.is_active,
    });
    const allRaw = rawMaterials as any[];
    const requiredIds = REQUIRED_MATERIAL_NAMES.map(
      (n) => allRaw.find((m) => m.name.toLowerCase().includes(n.toLowerCase()))?.id
    ).filter(Boolean) as number[];
    const concentrateIds = new Set<number>(
      allRaw.filter((m) => isConcentrateMaterial(m.name)).map((m) => m.id)
    );

    const existingRows: MaterialRow[] = (tpl.materials ?? []).map((m: any) => ({
      raw_material_id: m.raw_material_id,
      quantity_per_tonne: parseFloat(m.quantity_per_tonne),
      required: requiredIds.includes(m.raw_material_id) || concentrateIds.has(m.raw_material_id),
      concentrateOnly: concentrateIds.has(m.raw_material_id),
    }));

    const missingRequired = requiredIds
      .filter((id) => !existingRows.some((r) => r.raw_material_id === id))
      .map(
        (id) =>
          ({
            raw_material_id: id,
            quantity_per_tonne: 0,
            required: true,
            concentrateOnly: false,
          }) as MaterialRow
      );

    const missingConcentrate = !existingRows.some((r) => r.concentrateOnly);

    setMaterials([
      ...existingRows,
      ...missingRequired,
      ...(missingConcentrate
        ? [
            {
              raw_material_id: 0,
              quantity_per_tonne: 0,
              required: true,
              concentrateOnly: true,
            } as MaterialRow,
          ]
        : []),
    ]);
    setError('');
    setDialogOpen(true);
  };

  const openDuplicate = (tpl: any) => {
    setEditingTemplate(null);
    setFormData({
      name: `Copy of ${tpl.name}`,
      description: tpl.description ?? '',
      feed_type_id: tpl.feed_type_id,
      feed_brand_id: tpl.feed_brand_id,
      bird_procurement_ids: [],
      bird_ids: [],
      default_quantity_kg: tpl.default_quantity_kg
        ? String(parseInt(tpl.default_quantity_kg, 10))
        : '',
      other_costs: tpl.other_costs ?? '0',
      notes: tpl.notes ?? '',
      is_active: true,
    });
    const allRaw = rawMaterials as any[];
    const requiredIds = REQUIRED_MATERIAL_NAMES.map(
      (n) => allRaw.find((m) => m.name.toLowerCase().includes(n.toLowerCase()))?.id
    ).filter(Boolean) as number[];
    const concentrateIds = new Set<number>(
      allRaw.filter((m) => isConcentrateMaterial(m.name)).map((m) => m.id)
    );
    setMaterials(
      (tpl.materials ?? []).map((m: any) => ({
        raw_material_id: m.raw_material_id,
        quantity_per_tonne: parseFloat(m.quantity_per_tonne),
        required: requiredIds.includes(m.raw_material_id) || concentrateIds.has(m.raw_material_id),
        concentrateOnly: concentrateIds.has(m.raw_material_id),
      }))
    );
    setError('');
    setDialogOpen(true);
  };

  const openDetail = (tpl: any) => {
    setSelectedTemplate(tpl);
    setDetailDialogOpen(true);
  };

  const handleSave = async () => {
    setError('');
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.feed_type_id) {
      setError('Feed type is required');
      return;
    }
    if (!formData.feed_brand_id) {
      setError('Feed brand is required');
      return;
    }
    if (formData.bird_procurement_ids.length === 0 && formData.bird_ids.length === 0) {
      setError('Assign this template to at least one bird batch or bird group');
      return;
    }
    const incompleteRequired = materials.find(
      (m) =>
        m.required &&
        !(isEssaarConcentrate && fishMaterialId > 0 && m.raw_material_id === fishMaterialId) &&
        (m.raw_material_id === 0 || m.quantity_per_tonne <= 0)
    );
    if (incompleteRequired) {
      const label = incompleteRequired.concentrateOnly
        ? 'Concentrate (Essaar or Maridav)'
        : 'a required material';
      setError(`Quantity missing for ${label}`);
      return;
    }

    const validMaterials = materials.filter(
      (m) => m.raw_material_id > 0 && m.quantity_per_tonne > 0
    );
    if (!validMaterials.length) {
      setError('At least one material is required');
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description || undefined,
      feed_type_id: formData.feed_type_id,
      feed_brand_id: formData.feed_brand_id,
      default_quantity_kg: formData.default_quantity_kg
        ? parseFloat(String(formData.default_quantity_kg))
        : undefined,
      other_costs: parseFloat(String(formData.other_costs)) || 0,
      notes: formData.notes || undefined,
      is_active: formData.is_active,
      bird_procurement_ids: formData.bird_procurement_ids,
      bird_ids: formData.bird_ids,
      materials: validMaterials.map((m) => ({
        raw_material_id: m.raw_material_id,
        quantity_per_tonne: m.quantity_per_tonne,
      })),
    };

    try {
      if (editingTemplate) {
        await updateMutation.mutateAsync({ id: editingTemplate.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setDialogOpen(false);
      resetForm();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save template');
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    try {
      await deleteMutation.mutateAsync(selectedTemplate.id);
      setDeleteDialogOpen(false);
      setSelectedTemplate(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete template');
    }
  };

  const addMaterialRow = () => {
    setMaterials((prev) => [...prev, { raw_material_id: 0, quantity_per_tonne: 0 }]);
  };

  const removeMaterialRow = (index: number) => {
    setMaterials((prev) => prev.filter((row, i) => i !== index || row.required));
  };

  const updateMaterialRow = (index: number, field: keyof MaterialRow, value: number) => {
    setMaterials((prev) => prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const fishMaterialId =
    (rawMaterials as any[]).find((m) => m.name.toLowerCase().includes('fish'))?.id ?? 0;
  const concentrateRow = materials.find((m) => m.concentrateOnly);
  const concentrateName =
    concentrateRow && concentrateRow.raw_material_id > 0
      ? ((rawMaterials as any[]).find((m) => m.id === concentrateRow.raw_material_id)?.name ?? '')
      : '';
  const isEssaarConcentrate = concentrateName.toLowerCase().includes('essaar');

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Feed Production Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Reusable raw material compositions for feed production batches
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Template
        </Button>
      </Box>

      {/* Search + filter bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name, type, brand, or batch…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="all">
            All
            {templates.length > 0 && (
              <Chip
                label={templates.length}
                size="small"
                sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="active" sx={{ color: 'success.main' }}>
            Active
            {(templates as any[]).filter((t: any) => t.is_active).length > 0 && (
              <Chip
                label={(templates as any[]).filter((t: any) => t.is_active).length}
                size="small"
                color="success"
                sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="inactive" sx={{ color: 'text.secondary' }}>
            Inactive
            {(templates as any[]).filter((t: any) => !t.is_active).length > 0 && (
              <Chip
                label={(templates as any[]).filter((t: any) => !t.is_active).length}
                size="small"
                sx={{ ml: 0.75, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Table */}
      <Paper>
        {isFetching && (
          <Box sx={{ height: 3, bgcolor: 'primary.main', opacity: 0.6, borderRadius: 1 }} />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                <TableCell>Template</TableCell>
                <TableCell>Type · Brand</TableCell>
                <TableCell>Assigned to</TableCell>
                <TableCell>Qty / Materials</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {search || statusFilter !== 'all'
                      ? 'No templates match your search.'
                      : 'No templates yet. Create one to speed up feed production entry.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((tpl: any) => {
                  const batches: string[] = (tpl.bird_procurements ?? []).map(
                    (p: any) => p.identifier
                  );
                  const birds: string[] = (tpl.birds ?? []).map((b: any) => b.name ?? b.identifier);
                  return (
                    <TableRow key={tpl.id} hover sx={{ opacity: tpl.is_active ? 1 : 0.6 }}>
                      {/* Template name + description */}
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography fontWeight={600} sx={{ lineHeight: 1.3 }}>
                          {tpl.name}
                        </Typography>
                        {tpl.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}
                          >
                            {tpl.description}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Type · Brand */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {tpl.feed_type?.name ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tpl.feed_brand?.name ?? ''}
                        </Typography>
                      </TableCell>

                      {/* Batch / bird group chips */}
                      <TableCell sx={{ minWidth: 160 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {batches.length === 0 && birds.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">
                              —
                            </Typography>
                          ) : (
                            <>
                              {batches.map((b) => (
                                <Chip
                                  key={b}
                                  label={b}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              ))}
                              {birds.map((b) => (
                                <Chip
                                  key={b}
                                  label={b}
                                  size="small"
                                  sx={{ fontSize: '0.7rem', height: 20, bgcolor: 'action.hover' }}
                                />
                              ))}
                            </>
                          )}
                        </Box>
                      </TableCell>

                      {/* Qty + material count */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {tpl.default_quantity_kg
                            ? `${parseFloat(tpl.default_quantity_kg).toLocaleString()} kg`
                            : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tpl.materials?.length ?? 0} materials
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={tpl.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={tpl.is_active ? 'success' : 'default'}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => openDetail(tpl)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate for another batch">
                          <IconButton size="small" color="info" onClick={() => openDuplicate(tpl)}>
                            <DuplicateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(tpl)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedTemplate(tpl);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredTemplates.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              {filteredTemplates.length === templates.length
                ? `${templates.length} template${templates.length !== 1 ? 's' : ''}`
                : `${filteredTemplates.length} of ${templates.length} templates`}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'New Feed Production Template'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Name */}
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="Template Name"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                fullWidth
                required
              />
            </Grid>

            {/* Active toggle */}
            <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* Feed Type */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={feedTypes as any[]}
                getOptionLabel={(o) => o.name}
                value={(feedTypes as any[]).find((t) => t.id === formData.feed_type_id) ?? null}
                onChange={(_, v) => setFormData((p) => ({ ...p, feed_type_id: v?.id ?? 0 }))}
                renderInput={(params) => <TextField {...params} label="Feed Type" required />}
              />
            </Grid>

            {/* Feed Brand */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={feedBrands as any[]}
                getOptionLabel={(o) => o.name}
                value={(feedBrands as any[]).find((b) => b.id === formData.feed_brand_id) ?? null}
                onChange={(_, v) => setFormData((p) => ({ ...p, feed_brand_id: v?.id ?? 0 }))}
                renderInput={(params) => <TextField {...params} label="Feed Brand" required />}
              />
            </Grid>

            {/* Bird Batch */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                multiple
                options={birdProcurements as any[]}
                getOptionLabel={(o: any) => o.identifier}
                value={(birdProcurements as any[]).filter((p: any) =>
                  formData.bird_procurement_ids.includes(p.id)
                )}
                onChange={(_, v) => {
                  const newIds = (v as any[]).map((x) => x.id);
                  setFormData((p) => ({
                    ...p,
                    bird_procurement_ids: newIds,
                    // Drop any selected bird groups that no longer belong to a selected batch
                    bird_ids: p.bird_ids.filter((birdId) =>
                      (allBirds as any[]).some(
                        (b) => b.id === birdId && newIds.includes(b.procurement_id)
                      )
                    ),
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Bird Batches (whole batch)"
                    helperText="Applies to all groups in the batch"
                  />
                )}
              />
            </Grid>

            {/* Specific Bird Groups — scoped to selected batches */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                multiple
                disabled={formData.bird_procurement_ids.length === 0}
                options={(allBirds as any[]).filter((b: any) =>
                  formData.bird_procurement_ids.includes(b.procurement_id)
                )}
                getOptionLabel={(o: any) =>
                  `${o.name ?? o.identifier} (${o.birds_type ?? ''})`.trim()
                }
                value={(allBirds as any[]).filter((b: any) => formData.bird_ids.includes(b.id))}
                onChange={(_, v) =>
                  setFormData((p) => ({
                    ...p,
                    bird_ids: (v as any[]).map((x) => x.id),
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specific Bird Groups (optional)"
                    helperText={
                      formData.bird_procurement_ids.length === 0
                        ? 'Select a batch first'
                        : 'Overrides batch-level formula for these groups'
                    }
                  />
                )}
              />
            </Grid>

            {/* Default Quantity */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Default Quantity"
                value={formData.default_quantity_kg}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, default_quantity_kg: e.target.value }))
                }
                fullWidth
                helperText="Optional — pre-fills the production form"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="1000">1 tonne (1,000 kg)</MenuItem>
                <MenuItem value="2000">2 tonnes (2,000 kg)</MenuItem>
              </TextField>
            </Grid>

            {/* Other Costs */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Default Other Costs"
                type="number"
                value={formData.other_costs}
                onChange={(e) => setFormData((p) => ({ ...p, other_costs: e.target.value }))}
                fullWidth
                slotProps={{ htmlInput: { min: 0 } }}
              />
            </Grid>

            {/* Notes */}
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>

            {/* Materials */}
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Raw Materials per tonne produced
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addMaterialRow}>
                  Add Material
                </Button>
              </Box>

              {(() => {
                const totalQty = materials.reduce((sum, m) => sum + (m.quantity_per_tonne || 0), 0);
                const totalColor =
                  totalQty === 0
                    ? 'text.secondary'
                    : Math.abs(totalQty - 1000) < 0.001
                      ? 'success.main'
                      : totalQty > 1000
                        ? 'error.main'
                        : 'warning.main';
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'baseline',
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Total:
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color={totalColor}>
                      {totalQty.toLocaleString(undefined, { maximumFractionDigits: 3 })} kg
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      / 1,000 kg
                    </Typography>
                  </Box>
                );
              })()}

              {materials.map((row, idx) => {
                const baseOptions = (rawMaterials as any[]).filter(
                  (m) => !selectedMaterialIds.has(m.id) || m.id === row.raw_material_id
                );
                const availableMaterials = row.concentrateOnly
                  ? baseOptions.filter((m) => isConcentrateMaterial(m.name))
                  : baseOptions.filter((m) => !isConcentrateMaterial(m.name));

                const isFishRow = fishMaterialId > 0 && row.raw_material_id === fishMaterialId;
                if (isEssaarConcentrate && isFishRow) return null;
                const effectivelyRequired = row.required;

                return (
                  <Grid container spacing={1.5} key={idx} sx={{ mb: 1.5 }} alignItems="center">
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <Autocomplete
                        options={availableMaterials}
                        getOptionLabel={(o) => `${o.name} (${o.unit_of_measure})`}
                        value={
                          (rawMaterials as any[]).find((m) => m.id === row.raw_material_id) ?? null
                        }
                        onChange={(_, v) => updateMaterialRow(idx, 'raw_material_id', v?.id ?? 0)}
                        disabled={row.required && !row.concentrateOnly && !isFishRow}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={row.concentrateOnly ? 'Concentrate *' : 'Raw Material'}
                            size="small"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 10, sm: 5 }}>
                      <TextField
                        label="Qty / tonne *"
                        type="number"
                        size="small"
                        value={row.quantity_per_tonne || ''}
                        onChange={(e) =>
                          updateMaterialRow(
                            idx,
                            'quantity_per_tonne',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        fullWidth
                        slotProps={{ htmlInput: { min: 0.001, step: 0.001 } }}
                        error={effectivelyRequired && row.quantity_per_tonne <= 0}
                      />
                    </Grid>
                    <Grid size={{ xs: 2, sm: 1 }} sx={{ display: 'flex', alignItems: 'center' }}>
                      {effectivelyRequired ? (
                        <Tooltip title="Required material — cannot be removed">
                          <Chip
                            label="req"
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem' }}
                          />
                        </Tooltip>
                      ) : (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeMaterialRow(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDialogOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
          >
            {isSaving ? 'Saving…' : editingTemplate ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedTemplate?.name}</DialogTitle>
        <DialogContent dividers>
          {selectedTemplate && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Feed Type
                  </Typography>
                  <Typography fontWeight={600}>
                    {selectedTemplate.feed_type?.name ?? '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Brand
                  </Typography>
                  <Typography fontWeight={600}>
                    {selectedTemplate.feed_brand?.name ?? '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Bird Batches
                  </Typography>
                  <Typography fontWeight={600}>
                    {(selectedTemplate.bird_procurements ?? []).length > 0
                      ? (selectedTemplate.bird_procurements as any[])
                          .map((p: any) => p.identifier)
                          .join(', ')
                      : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Default Quantity
                  </Typography>
                  <Typography fontWeight={600}>
                    {selectedTemplate.default_quantity_kg
                      ? `${parseFloat(selectedTemplate.default_quantity_kg).toLocaleString()} kg`
                      : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Other Costs
                  </Typography>
                  <Typography fontWeight={600}>
                    {parseFloat(selectedTemplate.other_costs || 0).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'GHS',
                    })}
                  </Typography>
                </Grid>
                {selectedTemplate.description && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography>{selectedTemplate.description}</Typography>
                  </Grid>
                )}
                {selectedTemplate.notes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography>{selectedTemplate.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 3, mb: 1.5 }}>
                Raw Materials per tonne produced
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Qty / tonne
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedTemplate.materials?.map((m: any) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.raw_material?.name ?? m.raw_material_id}</TableCell>
                        <TableCell>{m.raw_material?.unit_of_measure ?? '—'}</TableCell>
                        <TableCell align="right">{parseFloat(m.quantity_per_tonne)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setDetailDialogOpen(false);
              openEdit(selectedTemplate);
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{selectedTemplate?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            startIcon={deleteMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
